
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Box, Button, ButtonGroup, Card, CardMedia, Checkbox, IconButton, Typography } from "@mui/material";
import { BoxFC, BoxFR } from "components/BoxCustom";
import { CloseRounded, DeleteRounded } from "resource/muiImport";
import { AddCircleRounded } from "resource/muiImport";
import ShareRounded from "@mui/icons-material/ShareRounded";
import AddJobOrderFileDialog from "../AddJobOrderFileDialog";
import { GlobalStateContext } from "contexts/GlobalStateContext";
import { jobOrderApiNm } from "constants/jobOrderConstant";
import { alertConfirmDelete } from "components/Alert";
import dayjs from "dayjs";
import FullScreenImage from "pages/FullScreenImage/FullScreenImage";
import { openLineShare } from "utils/openLineShare";

const emptyArr = []

/**
 * filesData   = job_order_file rows (deletable here unless readOnly). May span several job
 *               orders (branch ShippingInvoiceDialogV2 passes every file of the shipment), so
 *               a file's own JobOrdId wins over the jobOrdId prop.
 * pcFilesData = pc_item_file of the job order's petty-cash items (view/share only: they
 *               belong to the petty-cash item and are managed from there)
 * canShareJOData = show "พร้อมข้อมูลใบงาน" (joData must carry the shipment fields JobNo,
 *               Bkg, Agnt, JobSNm, as DailyJobDialogV3's getDailyJobAllDetailV4 does)
 */
const FilePreviewComponent = ({ filesData, pcFilesData = emptyArr, jobOrdId, joData, onPettyCashDialogFinish, readOnly
  , canShareJOData = false, label="ไฟล์แนบ", compact = false }) => {

  const { ax } = useContext(GlobalStateContext);
  const [selectedImage, setSelectedImage] = useState(null);
  const [dialogAddJobOrderFileOpen, setDialogAddJobOrderFileOpen] = useState(false);
  const [checkedKeys, setCheckedKeys] = useState([]); // file keys picked for LINE share

  // one list for both sources; Key is unique across them (JobOrdFileId and PCItmFileId can collide)
  const allFiles = useMemo(() => [
    ...filesData.map(file => ({ ...file, Src: "JO", Key: `JO-${file.JobOrdFileId}` })),
    ...pcFilesData.map(file => ({ ...file, Src: "PC", Key: `PC-${file.PCItmFileId}` })),
  ], [filesData, pcFilesData]);

  // drop picks of files that were deleted / reloaded away
  useEffect(() => {
    setCheckedKeys(o => o.filter(key => allFiles.some(file => file.Key === key)));
  }, [allFiles]);

  const toggleChecked = useCallback((key) => () => {
    setCheckedKeys(o => o.includes(key) ? o.filter(k => k !== key) : [...o, key]);
  }, []);

  const isAllChecked = allFiles.length > 0 && checkedKeys.length === allFiles.length;
  const handleCheckAll = useCallback(() => {
    setCheckedKeys(isAllChecked ? [] : allFiles.map(file => file.Key));
  }, [isAllChecked, allFiles]);

  const hasJOData = canShareJOData && !!joData?.JobOrdId;

  // send the picked files from the user's own LINE account via liff.shareTargetPicker;
  // withJOData puts a text message with the job order details in front of the images
  const handleShareLine = useCallback((withJOData) => () => {
    const msg = withJOData ? [
      `JobNo: ${joData.JobNo || "-"}`,
      `Booking: ${joData.Bkg || "-"}`,
      `Agent: ${joData.Agnt || "-"}`,
      `Job: ${joData.JobSNm || "-"}`,
      `Cont.No.: ${joData.ContNo || "-"}`,
      `SealNo.: ${joData.SealNo || "-"}`,
      `Size: ${joData.ContSizeFull || "-"}`,
      `Appointment Time: ${joData.AptTm ? dayjs(joData.AptTm).format("DD/MM/YYYY HH:mm") : "-"}`,
    ].join("\n") : "";
    const files = allFiles.filter(file => checkedKeys.includes(file.Key))
      .map(file => file.Src === "PC"
        ? { Src: "PC", PCItmId: file.PCItmId, FileNm: file.FileNm }
        : { Src: "JO", JobOrdId: file.JobOrdId || jobOrdId, FileNm: file.FileNm });
    // the server only accepts files of the job orders listed here
    const jobOrdIds = [...new Set([jobOrdId, ...files.map(file => file.JobOrdId)].filter(Boolean))];
    openLineShare(ax, {
      JobOrdIds: jobOrdIds,
      Files: files,
      msg: msg,
      Bkg: joData?.Bkg,
    });
  }, [ax, jobOrdId, joData, allFiles, checkedKeys]);

  const handleDeleteFile = useCallback((jobOrdFileId) => (e) => {
    e.stopPropagation(); // Prevent closing the preview
    alertConfirmDelete(() => {
      ax.post(jobOrderApiNm.deleteJobOrderFile, { JobOrdFileId: jobOrdFileId }).then((value) => {
        if (value.data) {
          setSelectedImage(null);
          onPettyCashDialogFinish && onPettyCashDialogFinish();
        }
      });
    });
  }, [ax, onPettyCashDialogFinish]);


  return (
    <BoxFC sx={compact ? { gap: 1 } : { gap: 0, height: 162 }}>
      <BoxFR sx={compact ? { flexWrap: "wrap", rowGap: 1 } : undefined}>
        <Typography fontWeight="bold">
          {label} ({filesData.length}{pcFilesData.length > 0 ? ` + เงินสดย่อย ${pcFilesData.length}` : ""})
        </Typography>
        {!readOnly && (compact ?
          <Button startIcon={<AddCircleRounded />} onClick={() => { setDialogAddJobOrderFileOpen(true) }}>เพิ่มไฟล์</Button>
          :
          <IconButton sx={{ p: 0 }} onClick={() => { setDialogAddJobOrderFileOpen(true) }}>
            <AddCircleRounded color="primary" />
          </IconButton>
        )}
        <Box flex={1} />
        {allFiles.length > 0 &&
          <>
            <Button size="small" sx={{ py: compact ? 0.75 : 0 }} onClick={handleCheckAll}>
              {isAllChecked ? "ยกเลิกเลือก" : "เลือกทั้งหมด"}
            </Button>
            <ButtonGroup disabled={checkedKeys.length === 0}>
            <Button size="small" variant="contained" color="success" sx={{ py: compact ? 0.75 : 0 }}
               onClick={handleShareLine(false)}>
              <ShareRounded fontSize="small" sx={{ mr: 0.5 }} />แชร์ LINE ({checkedKeys.length})
            </Button>
            {hasJOData &&
              <Button size="small" variant="contained" color="success" sx={{ py: compact ? 0.75 : 0 }}
                onClick={handleShareLine(true)}>
                พร้อมข้อมูลใบงาน
              </Button>
            }
            </ButtonGroup>
          </>
        }
      </BoxFR>
      {compact && allFiles.length === 0 &&
        <Typography sx={{ color: "text.secondary", py: 3, textAlign: "center" }}>
          ยังไม่มีไฟล์ในใบงานนี้{readOnly ? "" : " กด เพิ่มไฟล์ เพื่อถ่ายรูปหรือแนบไฟล์"}
        </Typography>
      }
      <Box flex={1} sx={compact ? undefined : { overflowX: "scroll" }}>
        <BoxFR sx={compact
          ? { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(104px, 1fr))", gap: 1, p: 0 }
          : { gap: 1, p: 0 }}>
          {allFiles.map((file) => {
            const isChecked = checkedKeys.includes(file.Key)
            return (
              <Card
                key={file.Key}
                sx={{
                  width: compact ? "100%" : 120,
                  height: compact ? "auto" : 120,
                  aspectRatio: compact ? "1 / 1" : undefined,
                  cursor: "pointer",
                  position: "relative",
                  flexShrink: 0,
                  outline: isChecked ? "3px solid" : "none",
                  outlineColor: "success.main",
                  outlineOffset: -3,
                  "&:hover": {
                    boxShadow: 4
                  }
                }}
                onClick={() => setSelectedImage(file)}
              >
                <CardMedia
                  component="img"
                  height={compact ? "100%" : "120"}
                  image={file.Url}
                  alt={file.FileNm}
                  sx={{ objectFit: "cover" }}
                />
                <Checkbox size={compact ? "medium" : "small"} checked={isChecked} onChange={toggleChecked(file.Key)}
                  onClick={(e) => e.stopPropagation()} // the card click opens the preview
                  inputProps={{ "aria-label": `เลือก ${file.FileNm || "ไฟล์"} เพื่อแชร์` }}
                  sx={{
                    position: "absolute", top: 2, left: 2, p: compact ? 0.75 : 0.25,
                    bgcolor: "rgba(255, 255, 255, 0.8)", borderRadius: 1,
                    "&:hover": { bgcolor: "white" },
                    "&.Mui-checked": { color: "success.main" }
                  }} />
                {file.Src === "PC" &&
                  <Typography variant="caption" noWrap
                    sx={{
                      position: "absolute", left: 0, right: 0, bottom: 0, px: 0.5,
                      color: "white", bgcolor: "rgba(0, 0, 0, 0.6)"
                    }}>
                    เงินสดย่อย: {file.ExpNm || file.Label || "-"}
                  </Typography>
                }
              </Card>
            )
          })}
        </BoxFR>
      </Box>

      {/* Full screen image preview */}
      {selectedImage && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "rgba(0, 0, 0, 0.9)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2
          }}
          onClick={() => setSelectedImage(null)}
        >
          <IconButton
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              color: "white"
            }}
            onClick={() => setSelectedImage(null)}
          >
            <CloseRounded />
          </IconButton>
          {/* petty-cash files are deleted from the petty-cash item, not here */}
          {!readOnly && selectedImage.Src === "JO" &&
            <IconButton
              sx={{
                position: "absolute",
                top: 16,
                left: 16,
                color: "white",
                bgcolor: "error.main",
                "&:hover": {
                  bgcolor: "error.dark"
                }
              }}
              onClick={handleDeleteFile(selectedImage.JobOrdFileId)}
            >
              <DeleteRounded />
            </IconButton>

          }

          <Typography
            sx={{
              position: "absolute",
              top: 16,
              left: "50%",
              transform: "translateX(-50%)",
              color: "white",
              bgcolor: "rgba(0, 0, 0, 0.5)",
              px: 2,
              py: 1,
              borderRadius: 1,
              zIndex: 1
            }}
          >
            {selectedImage.Src === "PC" ? "เงินสดย่อย: " : ""}
            {`${selectedImage.Label || ""}${selectedImage.DocNo ? ` (${selectedImage.DocNo})` : ""} by: ${selectedImage.DrvNNm || selectedImage.ModAccNNm || "-"} at ${selectedImage.AddTm ? dayjs(selectedImage.AddTm).format("DD/MM/YYYY HH:mm") : "-"}`}
          </Typography>
          <Box
            sx={{
              width: "100%",
              height: "95%",
              mt:6
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <FullScreenImage linkUrl={selectedImage.Url} width="100%" height="100%" />
          </Box>
        </Box>
      )}

      <AddJobOrderFileDialog
        selectedId={jobOrdId}
        selectedRow={joData}
        dialogOpen={dialogAddJobOrderFileOpen}
        setDialogOpen={setDialogAddJobOrderFileOpen}
        onFinish={onPettyCashDialogFinish} />
    </BoxFC>
  );
};

export default React.memo(FilePreviewComponent);
